const BASE_URL = '/api';
const TOKEN_KEY = 'zeus_token';
const REFRESH_KEY = 'zeus_refresh_token';

interface ApiResponse<T = unknown> {
  ok: boolean;
  data?: T;
  error?: string;
  status: number;
}

async function refreshToken(): Promise<boolean> {
  const refresh = localStorage.getItem(REFRESH_KEY);
  if (!refresh) return false;

  try {
    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: refresh }),
    });

    if (!res.ok) return false;

    const data = await res.json();
    if (data.token) {
      localStorage.setItem(TOKEN_KEY, data.token);
      if (data.refreshToken) {
        localStorage.setItem(REFRESH_KEY, data.refreshToken);
      }
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

function getHeaders(): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown
): Promise<ApiResponse<T>> {
  const url = `${BASE_URL}${path}`;

  try {
    let res = await fetch(url, {
      method,
      headers: getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });

    // Auto-refresh on 401
    if (res.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        res = await fetch(url, {
          method,
          headers: getHeaders(),
          body: body ? JSON.stringify(body) : undefined,
        });
      } else {
        // Clear tokens and redirect to login
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_KEY);
        window.location.href = '/auth';
        return { ok: false, error: 'Session expired', status: 401 };
      }
    }

    const contentType = res.headers.get('content-type');
    let data: T | undefined;

    if (contentType?.includes('application/json')) {
      try {
        data = await res.json();
      } catch {
        data = undefined;
      }
    }

    if (!res.ok) {
      const errorData = data as unknown as { error?: string; message?: string };
      return {
        ok: false,
        error: errorData?.error || errorData?.message || `Request failed (${res.status})`,
        status: res.status,
        data,
      };
    }

    return { ok: true, data, status: res.status };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'Network error',
      status: 0,
    };
  }
}

async function uploadFile(
  path: string,
  file: File,
  onProgress?: (pct: number) => void
): Promise<ApiResponse> {
  const url = `${BASE_URL}${path}`;
  const token = localStorage.getItem(TOKEN_KEY);

  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    formData.append('file', file);

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });

    xhr.addEventListener('load', () => {
      let data: unknown;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        data = undefined;
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ ok: true, data, status: xhr.status });
      } else {
        const errorData = data as { error?: string };
        resolve({
          ok: false,
          error: errorData?.error || `Upload failed (${xhr.status})`,
          status: xhr.status,
          data,
        });
      }
    });

    xhr.addEventListener('error', () => {
      resolve({ ok: false, error: 'Upload failed', status: 0 });
    });

    xhr.open('POST', url);
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    xhr.send(formData);
  });
}

export const api = {
  get: <T = unknown>(path: string) => request<T>('GET', path),
  post: <T = unknown>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T = unknown>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T = unknown>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T = unknown>(path: string) => request<T>('DELETE', path),
  upload: uploadFile,
};

export function setTokens(token: string, refreshTkn?: string) {
  localStorage.setItem(TOKEN_KEY, token);
  if (refreshTkn) {
    localStorage.setItem(REFRESH_KEY, refreshTkn);
  }
}

export function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function isAuthenticated(): boolean {
  return !!localStorage.getItem(TOKEN_KEY);
}

export type { ApiResponse };
